import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Wallet, Megaphone, Star, ShoppingCart, Calendar, Building2, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Bucket { label: string; icon: any; total: number; tone: string }

export default function RevenueDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['revenue-dashboard-30d'],
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const { data: invoices } = await supabase
        .from('payment_invoices' as any)
        .select('amount_etb, paid_at, advertising_packages(category, name)')
        .eq('status', 'paid').gte('paid_at', since);

      const { data: leadCharges } = await supabase
        .from('lead_charges' as any).select('amount_etb, status').eq('status', 'charged').gte('created_at', since);

      const { data: mortgage } = await supabase
        .from('mortgage_referrals' as any).select('payout_etb').eq('status', 'funded').gte('paid_at', since);

      const buckets: Record<string, number> = {
        tiered: 0, topup: 0, banner: 0, branding: 0, lead_wallet: 0, project: 0, event: 0,
      };
      ((invoices as any[]) || []).forEach((i) => {
        const cat = i.advertising_packages?.category || 'tiered';
        buckets[cat] = (buckets[cat] || 0) + Number(i.amount_etb || 0);
      });

      const leadRevenue = ((leadCharges as any[]) || []).reduce((s, c) => s + Number(c.amount_etb || 0), 0);
      const mortgageRevenue = ((mortgage as any[]) || []).reduce((s, m) => s + Number(m.payout_etb || 0), 0);

      return { buckets, leadRevenue, mortgageRevenue };
    },
  });

  if (isLoading) return <Skeleton className="h-96" />;

  const cards: Bucket[] = [
    { label: 'Tiered Subscriptions', icon: Users, total: data?.buckets.tiered || 0, tone: 'text-blue-600' },
    { label: 'Credit Top-ups', icon: ShoppingCart, total: data?.buckets.topup || 0, tone: 'text-orange-500' },
    { label: 'Banner Ads', icon: Megaphone, total: data?.buckets.banner || 0, tone: 'text-purple-600' },
    { label: 'Branding (Verified, Premium)', icon: Star, total: data?.buckets.branding || 0, tone: 'text-yellow-500' },
    { label: 'Featured Projects', icon: Building2, total: data?.buckets.project || 0, tone: 'text-emerald-600' },
    { label: 'Pay-per-Lead', icon: Wallet, total: data?.leadRevenue || 0, tone: 'text-red-500' },
    { label: 'Mortgage Referrals', icon: TrendingUp, total: data?.mortgageRevenue || 0, tone: 'text-cyan-600' },
    { label: 'Events & Expos', icon: Calendar, total: data?.buckets.event || 0, tone: 'text-pink-600' },
  ];

  const total = cards.reduce((s, c) => s + c.total, 0);

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-primary" /> Revenue Dashboard
        </h1>
        <p className="text-sm text-muted-foreground">Last 30 days · all 8 monetization levers</p>
      </div>

      <Card className="border-primary/30">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Total revenue (30d)</p>
          <p className="text-4xl font-bold mt-1">{total.toLocaleString()} ETB</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const pct = total > 0 ? Math.round((c.total / total) * 100) : 0;
          return (
            <Card key={c.label}>
              <CardContent className="p-4 space-y-2">
                <Icon className={`h-5 w-5 ${c.tone}`} />
                <p className="text-xs text-muted-foreground">{c.label}</p>
                <p className="text-2xl font-bold tabular-nums">{c.total.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">{pct}% of total</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
